import type { ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";
import { cn } from "../cn";

/** One choice in a select. */
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  /**
   * The choices, when they are a plain list.
   *
   * The console argued against this prop in writing — that a select wraps a real
   * `<select>`, so native `<option>` children are the correct shape, and an
   * `options` prop would then have to be maintained for every select in the
   * product. The first half is right and the second did not happen. What
   * happened instead is that four screens each wrote the same nine-line helper
   * to turn a list into `<option>` elements, byte for byte, and paid the
   * maintenance four times where nobody could see it.
   *
   * So this is additive rather than a replacement. `children` still works and is
   * what an `<optgroup>` or a placeholder row needs; `options` is for the case
   * that is only ever a list, which is every select in the console today.
   */
  options?: readonly SelectOption[];
}

/**
 * A native select.
 *
 * Native on purpose: it inherits keyboard behaviour, mobile pickers and screen
 * reader support that a custom listbox has to reimplement and usually gets
 * subtly wrong.
 */
export function Select({
  label,
  error,
  hint,
  options,
  className,
  id,
  children,
  ...props
}: SelectProps) {
  const generated = useId();
  const selectId = id ?? generated;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={selectId} className="font-sans text-ui text-ink-2">
          {label}
        </label>
      ) : null}
      {/* The chevron is a mask on this wrapper, not a background image on the
          control. A data-URI background cannot read a custom property, so its
          colour had to be a literal — one fixed grey for both themes, and one
          nothing could catch: check-tokens skips the token file, and `%23`
          hides a hex from its pattern anyway. As a mask the shape is alpha and
          `--color-ink-3` paints it, which is the role it was always playing. */}
      <div className="select-chevron relative">
        <select
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(error && errorId, hint && hintId) || undefined}
          className={cn(
            "h-9 w-full select-control rounded-[var(--radius-control)] border border-rule-strong bg-ground pr-9 pl-3 font-sans text-ui text-ink transition-colors hover:border-ink-3",
            "focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-critical",
            className,
          )}
          {...props}
        >
          {/*
          Children first, and it matters: the one realistic reason to mix the
          two is a placeholder row, and a browser selects the first option when
          nothing else is selected. Rendered after the list, a placeholder would
          sit at the bottom and the first real choice would be pre-selected —
          which reads as the user having answered a question they have not.
        */}
          {children}
          {options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {hint && !error ? (
        <p id={hintId} className="text-meta text-ink-3">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-meta text-critical-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}
